import type { ReactNode } from 'react';

import type { SubscriptionSummary } from '../api/types.js';
import type { TimelineScope } from '../state/timeline.js';

/**
 * The subscription sidebar (M1.15.1).
 *
 * ## Counts come from the server, always
 *
 * Every number here is one the server sent — from `GET /counts`, or from the
 * `StateChangeResult` a mutation returned. **Nothing is recomputed from the
 * items on screen.** D2 makes the server authoritative, and a client that
 * tallies its own list is wrong the moment an unread item is outside the
 * loaded page, which for any real feed list is immediately.
 *
 * ## Counts are hidden unless Inbox mode is on
 *
 * [D18](../../../Design-Decisions.md) and M1.18.1. The numbers are fetched and
 * held either way — turning the mode on is instant and shows real counts
 * rather than counts that start from the moment you asked.
 *
 * ## Collapsed folders are remembered per viewer
 *
 * Locally, because it is a display preference about this screen on this
 * device. Somebody with forty feeds collapses them once and expects that to
 * hold; making it a server round trip would be a write on every triangle.
 */

export interface Folder {
  readonly folderId: string;
  readonly name: string;
}

export interface SidebarProps {
  readonly subscriptions: readonly SubscriptionSummary[];
  readonly folders: readonly Folder[];
  /** Server-issued counts, keyed by subscription id. */
  readonly counts: ReadonlyMap<string, number>;
  readonly total: number;
  readonly scope: TimelineScope;
  readonly showCounts: boolean;
  readonly collapsed: readonly string[];
  readonly onSelect: (scope: TimelineScope) => void;
  readonly onToggleFolder: (folderId: string) => void;
  readonly onAddFeed: () => void;
}

const UNFILED = '__unfiled__';

export function Sidebar(props: SidebarProps): ReactNode {
  const grouped = new Map<string, SubscriptionSummary[]>();

  for (const subscription of props.subscriptions) {
    const key = subscription.folderId ?? UNFILED;
    grouped.set(key, [...(grouped.get(key) ?? []), subscription]);
  }

  const folderName = (folderId: string): string =>
    folderId === UNFILED
      ? 'Feeds'
      : (props.folders.find((folder) => folder.folderId === folderId)?.name ?? folderId);

  const unreadFor = (subscription: SubscriptionSummary): number =>
    props.counts.get(subscription.feedId) ?? subscription.unread;

  return (
    <nav className="sidebar" aria-label="Subscriptions">
      <div className="sidebar__header">
        <button className="button button--small" onClick={props.onAddFeed}>
          Add feed
        </button>
      </div>

      <ul className="sidebar__list">
        <li>
          <button
            className={`sidebar__row${props.scope.folderId === undefined && props.scope.feedId === undefined ? ' is-selected' : ''}`}
            onClick={() => {
              props.onSelect({ filter: props.scope.filter });
            }}
          >
            <span className="sidebar__name">All feeds</span>
            {props.showCounts && props.total > 0 && (
              <span className="sidebar__count">{props.total}</span>
            )}
          </button>
        </li>
      </ul>

      {[...grouped.entries()]
        .sort(([left], [right]) =>
          left === UNFILED ? 1 : right === UNFILED ? -1 : left.localeCompare(right),
        )
        .map(([folderId, feeds]) => {
          const isCollapsed = props.collapsed.includes(folderId);
          const folderUnread = feeds.reduce((sum, feed) => sum + unreadFor(feed), 0);

          return (
            <section className="sidebar__folder" key={folderId}>
              <h2 className="sidebar__folder-heading">
                <button
                  className="sidebar__row sidebar__row--folder"
                  aria-expanded={!isCollapsed}
                  onClick={() => {
                    props.onToggleFolder(folderId);
                  }}
                >
                  <span aria-hidden="true" className="sidebar__twisty">
                    {isCollapsed ? '▸' : '▾'}
                  </span>
                  <span className="sidebar__name">{folderName(folderId)}</span>
                  {props.showCounts && folderUnread > 0 && (
                    <span className="sidebar__count">{folderUnread}</span>
                  )}
                </button>
                {folderId !== UNFILED && (
                  <button
                    className="sidebar__scope"
                    onClick={() => {
                      props.onSelect({ filter: props.scope.filter, folderId });
                    }}
                    title={`Show only ${folderName(folderId)}`}
                  >
                    open
                  </button>
                )}
              </h2>

              {!isCollapsed && (
                <ul className="sidebar__list">
                  {feeds.map((subscription) => {
                    const unread = unreadFor(subscription);
                    return (
                      <li key={subscription.feedId}>
                        <button
                          className={`sidebar__row${props.scope.feedId === subscription.feedId ? ' is-selected' : ''}${unread === 0 ? ' is-quiet' : ''}`}
                          onClick={() => {
                            props.onSelect({
                              filter: props.scope.filter,
                              feedId: subscription.feedId,
                            });
                          }}
                        >
                          <span className="sidebar__name">{subscription.title}</span>
                          {/*
                           * A feed that has been failing for weeks must not
                           * look identical to one that is simply quiet — the
                           * distinction the health field exists for.
                           */}
                          {subscription.status !== undefined &&
                            subscription.status !== 'active' && (
                              <span
                                className="sidebar__health"
                                title={`This feed is ${subscription.status}`}
                              >
                                !
                              </span>
                            )}
                          {props.showCounts && unread > 0 && (
                            <span className="sidebar__count">{unread}</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          );
        })}
    </nav>
  );
}
